import { handleErrorAxios } from "@/common/lib/formatter";
import Http from "@/common/lib/http";

const TENANT_BRANDING_CACHE_TTL_MS = 10 * 60 * 1000;

const actions = {
    async getCachedTenantBrandingSettings({
        commit,
        dispatch,
        getters,
        state
    }) {
        if (getters.isTenantBrandingCacheValid) {
            return state.tenantBranding.data;
        }
        const data = await dispatch("getTenantBrandingSettings");
        commit("SET_TENANT_BRANDING", {
            data,
            expiredAt: Date.now() + TENANT_BRANDING_CACHE_TTL_MS
        });
        return data;
    },
    async listTenantsPaginated({ commit, dispatch }, params = {}) {
        try {
            const { data } = await Http.instance.get("/bo/tenants", { params });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async getTenant({ commit, dispatch }, { id }) {
        try {
            const { data } = await Http.instance.get(`/bo/tenants/${id}`);
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async getTenantBrandingSettings({ commit, dispatch }) {
        try {
            const { data } = await Http.instance.get("/bo/settings/branding");
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async updateTenantBrandingSettings(
        { commit, dispatch },
        {
            businessDescription,
            defaultCountry,
            logoHorizontalUrl,
            logoIconUrl,
            name,
            socialNetworks,
            theme,
            whatsappNumber
        }
    ) {
        commit("CLEAR_CATALOG_PREVIEW");
        try {
            const { data } = await Http.instance.patch(
                "/bo/settings/branding",
                {
                    businessDescription,
                    defaultCountry,
                    logoHorizontalUrl,
                    logoIconUrl,
                    name,
                    socialNetworks,
                    theme,
                    whatsappNumber
                }
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async updateCatalogDesign(
        { commit, dispatch },
        { productItemDesign, showSearchBar, stackColors }
    ) {
        commit("CLEAR_CATALOG_PREVIEW");
        try {
            const { data } = await Http.instance.patch("/bo/catalog", {
                stackColors,
                showSearchBar,
                productItemDesign
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async getTenantProductTypes({ commit, dispatch }) {
        try {
            const { data } = await Http.instance.get(
                "/bo/settings/product-types"
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async updateTenantProductTypes({ commit, dispatch }, payload) {
        try {
            const { data } = await Http.instance.patch(
                "/bo/settings/product-types",
                payload
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async getTenantPaymentMethodsSettings({ commit, dispatch }) {
        try {
            const { data } = await Http.instance.get(
                "/bo/settings/payment-methods"
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async updateTenantPaymentMethodsSettings({ commit, dispatch }, payload) {
        try {
            const { data } = await Http.instance.patch(
                "/bo/settings/payment-methods",
                payload
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async getCatalogPreview({ commit, dispatch }) {
        try {
            const { data } = await Http.instance.get("/bo/catalog");
            // En Modo Admin la sesión trae el slug del admin, no el del
            // tenant seleccionado: sin esto $publicUrl arma mal la URL.
            if (data.tenantSlug) {
                commit("auth/SET_TENANT_SLUG", data.tenantSlug, { root: true });
            }
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async updateTenantSegments({ commit, dispatch }, payload) {
        commit("CLEAR_CATALOG_PREVIEW");
        try {
            const { data } = await Http.instance.put(
                "/bo/catalog/segments",
                payload
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async updateTenantDisplayMode({ commit, dispatch }, payload) {
        commit("CLEAR_CATALOG_PREVIEW");
        try {
            const { data } = await Http.instance.patch(
                "/bo/catalog/display-mode",
                payload
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async listCatalogVersions({ commit, dispatch }) {
        try {
            const { data } = await Http.instance.get("/bo/catalog/versions");
            return data.versions;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async getCatalogVersion({ commit, dispatch }, { id }) {
        try {
            const { data } = await Http.instance.get(
                `/bo/catalog/versions/${id}`
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async createCatalogVersion({ commit, dispatch }, { label }) {
        try {
            const { data } = await Http.instance.post("/bo/catalog/versions", {
                label
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async restoreCatalogVersion({ commit, dispatch }, { id }) {
        commit("CLEAR_CATALOG_PREVIEW");
        try {
            const { data } = await Http.instance.post(
                `/bo/catalog/versions/${id}/restore`
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async getTenantOrdersSettings({ commit, dispatch }) {
        try {
            const { data } = await Http.instance.get("/bo/settings/orders");
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async updateTenantOrdersSettings({ commit, dispatch }, payload) {
        try {
            const { data } = await Http.instance.patch(
                "/bo/settings/orders",
                payload
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async getTenantInventorySettings({ commit, dispatch }) {
        try {
            const { data } = await Http.instance.get("/bo/settings/inventory");
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async updateTenantInventorySettings({ commit, dispatch }, payload) {
        try {
            const { data } = await Http.instance.patch(
                "/bo/settings/inventory",
                payload
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    // sucursales-inventario-multitenant: body `{ inventoryMode, storeId?,
    // syncFromStores? }` (ver `buildInventoryModePayload`).
    async changeTenantInventoryMode({ commit, dispatch }, payload) {
        try {
            const { data } = await Http.instance.patch(
                "/bo/settings/inventory/mode",
                payload
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async getTenantTagsSettings({ commit, dispatch }) {
        try {
            const { data } = await Http.instance.get("/bo/settings/tags");
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async updateTenantTagsSettings({ commit, dispatch }, payload) {
        try {
            const { data } = await Http.instance.patch(
                "/bo/settings/tags",
                payload
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async getTenantFiltersSettings({ commit, dispatch }) {
        try {
            const { data } = await Http.instance.get("/bo/settings/filters");
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async updateTenantFiltersSettings({ commit, dispatch }, payload) {
        try {
            const { data } = await Http.instance.patch(
                "/bo/settings/filters",
                payload
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    // billing-fase5-salida-planes: booleanos manuales ("Mis funciones").
    async getFeatureFlagsSettings({ commit, dispatch }) {
        try {
            const { data } = await Http.instance.get(
                "/bo/settings/feature-flags"
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async updateFeatureFlagsSettings({ commit, dispatch }, payload) {
        commit("CLEAR_CATALOG_PREVIEW");
        try {
            const { data } = await Http.instance.patch(
                "/bo/settings/feature-flags",
                payload
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async getWatermarkSettings({ commit, dispatch }) {
        try {
            const { data } = await Http.instance.get("/bo/settings/watermark");
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    },
    async updateWatermarkSettings({ commit, dispatch }, payload) {
        commit("CLEAR_CATALOG_PREVIEW");
        try {
            const { data } = await Http.instance.patch(
                "/bo/settings/watermark",
                payload
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_TENANTS", parseError);
            throw parseError;
        }
    }
};

export default actions;
